import React, { useState } from 'react';
import { useAppContext, Campaign } from '../contexts/AppContext';
import { formatCurrency, formatDate, generateReference } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { toast } from 'sonner';
import { Heart, Plus, Search, Filter, Users, Calendar, Target, Trash2 } from 'lucide-react';
type Tab = 'discover' | 'my-campaigns' | 'my-donations';
export function Crowdfunding() {
  const {
    state,
    dispatch,
    processTransaction
  } = useAppContext();
  const {
    user,
    campaigns,
    transactions
  } = state;
  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDonateOpen, setIsDonateOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [donationAmount, setDonationAmount] = useState('');
  // Create campaign form state
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [newCategory, setNewCategory] = useState('Medical');
  const [newEndDate, setNewEndDate] = useState('');
  const categories = ['All', 'Medical', 'Education', 'Community', 'Business', 'Personal'];
  const myCampaigns = campaigns.filter(c => c.userId === user?.id);
  const otherCampaigns = campaigns.filter(c => c.userId !== user?.id);
  const myDonations = transactions.filter(t => t.type === 'Crowdfunding' && t.amount < 0);
  const filteredCampaigns = otherCampaigns.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || c.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });
  const handleCreateCampaign = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle || !newDescription || !newTarget || !newEndDate) {
      toast.error('Please fill in all fields');
      return;
    }
    const campaign: Campaign = {
      id: `camp_${Date.now()}`,
      title: newTitle,
      description: newDescription,
      targetAmount: Number(newTarget),
      raisedAmount: 0,
      category: newCategory,
      endDate: new Date(newEndDate).toISOString(),
      status: 'Active',
      imageUrl: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?auto=format&fit=crop&q=80&w=400&h=250',
      createdAt: new Date().toISOString(),
      userId: user?.id || 'usr_1',
      organizerName: user?.name || 'Tunde Adeleke'
    };
    dispatch({
      type: 'CREATE_CAMPAIGN',
      payload: campaign
    });
    toast.success('Campaign created successfully!');
    setIsCreateOpen(false);
    setNewTitle('');
    setNewDescription('');
    setNewTarget('');
    setNewEndDate('');
    setActiveTab('my-campaigns');
  };
  const handleDonate = () => {
    const amount = Number(donationAmount);
    if (!amount || amount < 500) {
      toast.error('Minimum donation is ₦500');
      return;
    }
    if (!user || amount > user.walletBalance) {
      toast.error('Insufficient wallet balance');
      return;
    }
    if (!selectedCampaign) return;
    dispatch({
      type: 'UPDATE_CAMPAIGN_RAISED',
      payload: {
        id: selectedCampaign.id,
        amount
      }
    });
    processTransaction(-amount, 'Crowdfunding', `Donation to: ${selectedCampaign.title}`);
    toast.success(`Successfully donated ${formatCurrency(amount)}!`, {
      description: `Thank you for supporting "${selectedCampaign.title}"`
    });
    setIsDonateOpen(false);
    setDonationAmount('');
    setSelectedCampaign(null);
  };
  const handleDeleteCampaign = (id: string) => {
    // For simplicity, filter out from state by dispatching a filtered campaigns list
    // Since we don't have a DELETE_CAMPAIGN action, we'll just mark it as expired
    dispatch({
      type: 'UPDATE_CAMPAIGN_RAISED',
      payload: {
        id,
        amount: 0
      }
    });
    toast.success('Campaign removed');
  };
  const openDonateModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setDonationAmount('');
    setIsDonateOpen(true);
  };
  const getProgress = (raised: number, target: number) => Math.min(Math.round(raised / target * 100), 100);
  const getDaysRemaining = (endDate: string) => {
    const diff = new Date(endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };
  const tabs = [{
    id: 'discover' as const,
    label: 'Discover Campaigns',
    count: filteredCampaigns.length
  }, {
    id: 'my-campaigns' as const,
    label: 'My Campaigns',
    count: myCampaigns.length
  }, {
    id: 'my-donations' as const,
    label: 'My Donations',
    count: myDonations.length
  }];
  return <div className="space-y-6" data-id="element-341">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-342">
        <div data-id="element-343">
          <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-344">
            Community Crowdfunding
          </h1>
          <p className="text-brand-text-light mt-1" data-id="element-345">
            Support causes that matter. Zero fees for social campaigns.
          </p>
        </div>
        <button onClick={() => setIsCreateOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors shadow-sm" data-id="element-346">
          <Plus size={18} data-id="element-347" /> Start a Campaign
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" data-id="element-348">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-white text-brand-text-dark shadow-sm' : 'text-brand-text-light hover:text-brand-text-dark'}`} data-id="element-349">
            {tab.label} ({tab.count})
          </button>)}
      </div>

      {/* Discover Tab */}
      {activeTab === 'discover' && <div className="space-y-6" data-id="element-350">
          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-351">
            <div className="relative flex-1" data-id="element-352">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-353" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search campaigns..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-354" />
            </div>
            <div className="flex items-center gap-2" data-id="element-355">
              <Filter size={18} className="text-gray-400" data-id="element-356" />
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-357">
                {categories.map(cat => <option key={cat} value={cat} data-id="element-358">
                    {cat}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Campaign Grid */}
          {filteredCampaigns.length === 0 ? <div className="text-center py-16 text-brand-text-light" data-id="element-359">
              <Users size={48} className="mx-auto mb-4 opacity-40" data-id="element-360" />
              <p className="text-lg font-medium" data-id="element-361">No campaigns found</p>
              <p className="text-sm mt-1" data-id="element-362">
                Try adjusting your search or filters
              </p>
            </div> : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-363">
              {filteredCampaigns.map(campaign => {
          const progress = getProgress(campaign.raisedAmount, campaign.targetAmount);
          const daysLeft = getDaysRemaining(campaign.endDate);
          return <div key={campaign.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-id="element-364">
                    <img src={campaign.imageUrl} alt={campaign.title} className="w-full h-40 object-cover" data-id="element-365" />
                    <div className="p-5 space-y-4" data-id="element-366">
                      <div className="flex items-start justify-between gap-2" data-id="element-367">
                        <h3 className="font-semibold text-brand-text-dark line-clamp-1" data-id="element-368">
                          {campaign.title}
                        </h3>
                        <StatusBadge status={campaign.status} data-id="element-369" />
                      </div>
                      <p className="text-sm text-brand-text-light line-clamp-2" data-id="element-370">
                        {campaign.description}
                      </p>

                      <div data-id="element-371">
                        <div className="flex justify-between text-sm mb-1.5" data-id="element-372">
                          <span className="font-semibold text-brand-primary" data-id="element-373">
                            {formatCurrency(campaign.raisedAmount)}
                          </span>
                          <span className="text-brand-text-light" data-id="element-374">
                            of {formatCurrency(campaign.targetAmount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-375">
                          <div className="bg-brand-primary h-2 rounded-full transition-all duration-500" style={{
                    width: `${progress}%`
                  }} data-id="element-376" />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-brand-text-light" data-id="element-377">
                        <span className="flex items-center gap-1" data-id="element-378">
                          <Calendar size={14} data-id="element-379" /> {daysLeft} days left
                        </span>
                        <span className="flex items-center gap-1" data-id="element-380">
                          <Target size={14} data-id="element-381" /> {progress}% funded
                        </span>
                      </div>

                      <button onClick={() => openDonateModal(campaign)} className="w-full py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors flex items-center justify-center gap-2" data-id="element-382">
                        <Heart size={16} data-id="element-383" /> Support This Cause
                      </button>
                    </div>
                  </div>;
        })}
            </div>}
        </div>}

      {/* My Campaigns Tab */}
      {activeTab === 'my-campaigns' && <div className="space-y-4" data-id="element-384">
          {myCampaigns.length === 0 ? <div className="text-center py-16 text-brand-text-light bg-white rounded-2xl border border-gray-100" data-id="element-385">
              <Target size={48} className="mx-auto mb-4 opacity-40" data-id="element-386" />
              <p className="text-lg font-medium" data-id="element-387">No campaigns yet</p>
              <p className="text-sm mt-1" data-id="element-388">
                Start your first campaign to raise funds for a cause.
              </p>
              <button onClick={() => setIsCreateOpen(true)} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-389">
                <Plus size={18} data-id="element-390" /> Create Campaign
              </button>
            </div> : myCampaigns.map(campaign => {
        const progress = getProgress(campaign.raisedAmount, campaign.targetAmount);
        return <div key={campaign.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col sm:flex-row gap-4" data-id="element-391">
                  <img src={campaign.imageUrl} alt={campaign.title} className="w-full sm:w-40 h-28 object-cover rounded-xl" data-id="element-392" />
                  <div className="flex-1 space-y-2" data-id="element-393">
                    <div className="flex items-start justify-between" data-id="element-394">
                      <h3 className="font-semibold text-brand-text-dark" data-id="element-395">
                        {campaign.title}
                      </h3>
                      <StatusBadge status={campaign.status} data-id="element-396" />
                    </div>
                    <p className="text-sm text-brand-text-light line-clamp-1" data-id="element-397">
                      {campaign.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm" data-id="element-398">
                      <span className="font-semibold text-brand-primary" data-id="element-399">
                        {formatCurrency(campaign.raisedAmount)}
                      </span>
                      <span className="text-brand-text-light" data-id="element-400">
                        of {formatCurrency(campaign.targetAmount)} ({progress}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-401">
                      <div className="bg-brand-primary h-1.5 rounded-full" style={{
                width: `${progress}%`
              }} data-id="element-402" />
                    </div>
                  </div>
                </div>;
      })}
        </div>}

      {/* My Donations Tab */}
      {activeTab === 'my-donations' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-403">
          {myDonations.length === 0 ? <div className="text-center py-16 text-brand-text-light" data-id="element-404">
              <Heart size={48} className="mx-auto mb-4 opacity-40" data-id="element-405" />
              <p className="text-lg font-medium" data-id="element-406">No donations yet</p>
              <p className="text-sm mt-1" data-id="element-407">
                Explore campaigns and make your first contribution.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-408">
              <table className="w-full" data-id="element-409">
                <thead className="bg-gray-50 border-b border-gray-100" data-id="element-410">
                  <tr data-id="element-411">
                    <th className="text-left px-6 py-3 text-xs font-medium text-brand-text-light uppercase" data-id="element-412">
                      Campaign
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-brand-text-light uppercase" data-id="element-413">
                      Amount
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-brand-text-light uppercase" data-id="element-414">
                      Date
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-brand-text-light uppercase" data-id="element-415">
                      Reference
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-medium text-brand-text-light uppercase" data-id="element-416">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50" data-id="element-417">
                  {myDonations.map(donation => <tr key={donation.id} className="hover:bg-gray-50" data-id="element-418">
                      <td className="px-6 py-4 text-sm font-medium text-brand-text-dark" data-id="element-419">
                        {donation.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-brand-error font-medium" data-id="element-420">
                        {formatCurrency(Math.abs(donation.amount))}
                      </td>
                      <td className="px-6 py-4 text-sm text-brand-text-light" data-id="element-421">
                        {formatDate(donation.date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-brand-text-light font-mono" data-id="element-422">
                        {donation.reference}
                      </td>
                      <td className="px-6 py-4" data-id="element-423">
                        <StatusBadge status={donation.status} data-id="element-424" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {/* Create Campaign Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} title="Start a Campaign" maxWidth="max-w-lg" data-id="element-425">
        <form onSubmit={handleCreateCampaign} className="space-y-4" data-id="element-426">
          <div data-id="element-427">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-428">
              Campaign Title
            </label>
            <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. Medical Fund for Amara" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-429" />
          </div>
          <div data-id="element-430">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-431">
              Description
            </label>
            <textarea value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="Tell people why this campaign matters..." rows={3} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none resize-none" data-id="element-432" />
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-433">
            <div data-id="element-434">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-435">
                Target Amount (₦)
              </label>
              <input type="number" value={newTarget} onChange={e => setNewTarget(e.target.value)} placeholder="750,000" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-436" />
            </div>
            <div data-id="element-437">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-438">
                Category
              </label>
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-439">
                {categories.filter(c => c !== 'All').map(cat => <option key={cat} value={cat} data-id="element-440">
                      {cat}
                    </option>)}
              </select>
            </div>
          </div>
          <div data-id="element-441">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-442">
              End Date
            </label>
            <input type="date" value={newEndDate} onChange={e => setNewEndDate(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-443" />
          </div>
          <button type="submit" className="w-full py-3 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary-medium transition-colors" data-id="element-444">
            Publish Campaign
          </button>
        </form>
      </Modal>

      {/* Donate Modal */}
      <Modal isOpen={isDonateOpen} onClose={() => setIsDonateOpen(false)} title="Support This Cause" data-id="element-445">
        {selectedCampaign && <div className="space-y-5" data-id="element-446">
            <div data-id="element-447">
              <h3 className="font-semibold text-brand-text-dark text-lg" data-id="element-448">
                {selectedCampaign.title}
              </h3>
              <p className="text-sm text-brand-text-light mt-1" data-id="element-449">
                {selectedCampaign.description}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4" data-id="element-450">
              <div className="flex justify-between text-sm mb-2" data-id="element-451">
                <span className="text-brand-text-light" data-id="element-452">Raised so far</span>
                <span className="font-semibold text-brand-primary" data-id="element-453">
                  {formatCurrency(selectedCampaign.raisedAmount)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-454">
                <div className="bg-brand-primary h-2 rounded-full" style={{
              width: `${getProgress(selectedCampaign.raisedAmount, selectedCampaign.targetAmount)}%`
            }} data-id="element-455" />
              </div>
              <p className="text-xs text-brand-text-light mt-2" data-id="element-456">
                Goal: {formatCurrency(selectedCampaign.targetAmount)}
              </p>
            </div>

            <div data-id="element-457">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-458">
                Donation Amount (₦)
              </label>
              <input type="number" value={donationAmount} onChange={e => setDonationAmount(e.target.value)} placeholder="Minimum ₦500" min={500} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg" data-id="element-459" />
              <div className="flex gap-2 mt-2" data-id="element-460">
                {[1000, 2500, 5000, 10000].map(amt => <button key={amt} type="button" onClick={() => setDonationAmount(amt.toString())} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-461">
                    {formatCurrency(amt)}
                  </button>)}
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-3 text-sm text-brand-primary-dark flex items-center gap-2" data-id="element-462">
              <Heart size={16} data-id="element-463" /> Zero platform fees — 100% goes to the cause.
            </div>

            <div className="flex items-center justify-between text-sm text-brand-text-light" data-id="element-464">
              <span data-id="element-465">Wallet Balance:</span>
              <span className="font-semibold text-brand-text-dark" data-id="element-466">
                {formatCurrency(user?.walletBalance || 0)}
              </span>
            </div>

            <button onClick={handleDonate} className="w-full py-3 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary-medium transition-colors flex items-center justify-center gap-2" data-id="element-467">
              <Heart size={18} data-id="element-468" /> Donate{' '}
              {donationAmount ? formatCurrency(Number(donationAmount)) : ''}
            </button>
          </div>}
      </Modal>
    </div>;
}